// Default Origination URIs the console provisions for static-host platforms
// (Retell, Vapi, ElevenLabs, xAI) so inbound trunk creation needs no URI step.
// Matching is by the stored URI string alone: a platform default never carries
// credentials, so the host-plus-auth ambiguity that rules out string matching
// for Custom URIs cannot arise here.

import type { ROriginationURIInternal } from 'v2/types/api/settings'
import {
  buildStaticSipUri,
  hasDefaultPlatformUris,
  matchesPlatformUri,
  SIP_PLATFORMS,
  type SipPlatformKey,
  type SipTransport,
} from './platform-config'
import type { UnifiedApplicationFormValues } from './types'

const MAX_NAME_ATTEMPTS = 5

export interface DefaultUriSpec {
  transport: SipTransport
  uri: string
  name: string
}

export interface DefaultUriPayload {
  name: string
  uri: string
}

/** Stored URIs are host-only strings; compare them case- and space-insensitively. */
export const normalizeStoredUri = (uri: string): string => uri.trim().toLowerCase()

export const defaultPlatformUriName = (key: SipPlatformKey, transport: SipTransport): string =>
  `${SIP_PLATFORMS[key].defaultUriLabel ?? SIP_PLATFORMS[key].label} (${transport.toUpperCase()})`

/** One default URI per transport the platform supports, in config order. */
export const buildDefaultPlatformUris = (
  key: SipPlatformKey | undefined,
  region?: string,
): DefaultUriSpec[] => {
  if (!key || !hasDefaultPlatformUris(key)) return []
  return (SIP_PLATFORMS[key].transports ?? []).flatMap((transport) => {
    const uri = buildStaticSipUri(key, transport, region)
    return uri ? [{ transport, uri, name: defaultPlatformUriName(key, transport) }] : []
  })
}

/**
 * The account URI to auto-select for a platform: the one whose stored string is
 * the platform's default-transport URI. Prefers a copy without auth, then the
 * oldest, so the pick is deterministic when a customer has duplicates.
 */
export const findDefaultPlatformUri = (
  key: SipPlatformKey | undefined,
  region: string | undefined,
  uris: ROriginationURIInternal[],
): ROriginationURIInternal | undefined => {
  if (!key || !hasDefaultPlatformUris(key)) return undefined
  const transport = SIP_PLATFORMS[key].defaultTransport
  if (!transport) return undefined
  const target = normalizeStoredUri(buildStaticSipUri(key, transport, region))
  return uris
    .filter((uri) => normalizeStoredUri(uri.uri) === target)
    .sort((a, b) => Number(a.is_auth) - Number(b.is_auth) || a.id - b.id)[0]
}

/**
 * Defaults the account is missing. A customer URI that already carries the
 * exact string counts as present whatever its name or auth. Names step to
 * "Vapi (UDP) 2" when a customer URI with a different host already took the
 * name: core enforces no uniqueness, trunk-central might.
 */
export const resolveMissingDefaultUris = (
  key: SipPlatformKey | undefined,
  region: string | undefined,
  existing: ROriginationURIInternal[],
): { createPayloads: DefaultUriPayload[] } => {
  const existingUris = new Set(existing.map((uri) => normalizeStoredUri(uri.uri)))
  const takenNames = new Set(existing.map((uri) => uri.name))
  const createPayloads: DefaultUriPayload[] = []
  for (const spec of buildDefaultPlatformUris(key, region)) {
    if (existingUris.has(normalizeStoredUri(spec.uri))) continue
    let name = spec.name
    for (let attempt = 2; takenNames.has(name) && attempt <= MAX_NAME_ATTEMPTS; attempt++) {
      name = `${spec.name} ${attempt}`
    }
    if (takenNames.has(name)) continue
    takenNames.add(name)
    createPayloads.push({ name, uri: spec.uri })
  }
  return { createPayloads }
}

/**
 * Core answers an account with ZERO Origination URIs with 400 "URIs not found"
 * instead of an empty list. For provisioning that is the most important case of
 * all (a fresh account), and for the dropdown it is just "nothing yet".
 */
export const isEmptyOriginationUriListError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)
    ?.response
  if (!response || response.status !== 400) return false
  const body = response.data as
    | { errors?: { global_error?: unknown }; data?: { error?: unknown }; message?: unknown }
    | undefined
  const texts = [body?.errors?.global_error, body?.data?.error, body?.message]
  return texts.some((t) => typeof t === 'string' && /uris? not found/i.test(t))
}

export type UriListFetcher = () => Promise<{ data?: ROriginationURIInternal[] }>
export type UriCreator = (payload: DefaultUriPayload) => Promise<unknown>

export interface EnsureDefaultUrisResult {
  created: number
  failed: number
}

/**
 * Idempotent provisioning: list, create what is missing, and on any create
 * error re-list once before calling it a failure. A rejected create whose URI
 * is present on the fresh list was a race (another tab, the modal) or a
 * uniqueness constraint in trunk-central, and the customer already has what
 * they need. Never throws; the caller decides how to surface `failed`.
 */
export const ensureDefaultPlatformUris = async ({
  platform,
  region,
  listUris,
  createUri,
}: {
  platform: SipPlatformKey
  region?: string
  listUris: UriListFetcher
  createUri: UriCreator
}): Promise<EnsureDefaultUrisResult> => {
  let existing: ROriginationURIInternal[]
  try {
    existing = (await listUris()).data ?? []
  } catch {
    return { created: 0, failed: buildDefaultPlatformUris(platform, region).length }
  }
  const { createPayloads } = resolveMissingDefaultUris(platform, region, existing)
  let created = 0
  const rejected: DefaultUriPayload[] = []
  for (const payload of createPayloads) {
    try {
      await createUri(payload)
      created++
    } catch {
      rejected.push(payload)
    }
  }
  if (rejected.length === 0) return { created, failed: 0 }
  try {
    const fresh = (await listUris()).data ?? []
    const stillMissing = new Set(
      resolveMissingDefaultUris(platform, region, fresh).createPayloads.map((p) =>
        normalizeStoredUri(p.uri),
      ),
    )
    const failed = rejected.filter((p) => stillMissing.has(normalizeStoredUri(p.uri))).length
    return { created, failed }
  } catch {
    return { created, failed: rejected.length }
  }
}

/**
 * The created record out of a create-URI response. Core forwards Zentrunk's raw
 * body, so accept the flat record, a `uri` / `data` wrapper, or nothing usable.
 */
export const extractCreatedUri = (body: unknown): ROriginationURIInternal | undefined => {
  const isRecord = (v: unknown): v is ROriginationURIInternal =>
    !!v &&
    typeof v === 'object' &&
    typeof (v as { id?: unknown }).id === 'number' &&
    typeof (v as { uri?: unknown }).uri === 'string'
  if (isRecord(body)) return body
  const wrapper = body as { uri?: unknown; data?: unknown } | undefined
  if (isRecord(wrapper?.uri)) return wrapper.uri
  if (isRecord(wrapper?.data)) return wrapper.data
  return undefined
}

/**
 * A URI just created from the modal becomes the Primary URI only when Primary
 * is still empty (never override a pick or an existing trunk's URI) and the URI
 * belongs to the selected platform. Custom and "no platform" accept anything.
 */
export const shouldSelectCreatedPrimaryUri = (
  values: UnifiedApplicationFormValues,
  uri: ROriginationURIInternal,
): boolean => {
  if (values.application_type !== 'sip' || values.trunk_direction !== 'inbound') return false
  if (typeof uri?.id !== 'number' || typeof uri.uri !== 'string') return false
  if ('primary_uri' in values && values.primary_uri) return false
  const platform = values.sip_platform as SipPlatformKey | undefined
  return !platform || matchesPlatformUri(uri.uri, platform)
}
